import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Input,
  Button,
  Card,
  Spin,
  Alert,
  Row,
  Col,
  message,
  Space,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { setupMfa, verifyMfa, type MfaSetupData } from '../../services/adminKycApiService';

const { Title, Text } = Typography;

/** MFA setup page — shown after first login if 2FA is not yet configured. */
export function MfaSetupPage() {
  const navigate = useNavigate();
  const [mfaData, setMfaData] = useState<MfaSetupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [totpCode, setTotpCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [setupError, setSetupError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setupMfa()
      .then(setMfaData)
      .catch(() => setSetupError(true))
      .finally(() => setLoading(false));
  }, []);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (totpCode.length === 6) {
      void handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totpCode]);

  const handleVerify = async () => {
    if (totpCode.length !== 6) return;
    setVerifyLoading(true);
    try {
      await verifyMfa(totpCode);
      void message.success('Two-factor authentication enabled successfully');
      navigate('/');
    } catch {
      void message.error('Invalid code — please try again');
      setTotpCode('');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    if (!mfaData) return;
    try {
      await navigator.clipboard.writeText(mfaData.backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      void message.success('Backup codes copied to clipboard');
    } catch {
      void message.error('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (setupError || !mfaData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card style={{ maxWidth: 480, width: '100%', borderRadius: 12 }}>
          <Alert type="error" message="Failed to load MFA setup. Please refresh and try again." showIcon />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f5f5',
      padding: 24,
    }}>
      <Card style={{ maxWidth: 560, width: '100%', borderRadius: 12 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/tap2serve.png" alt="Tap2Serve" style={{ width: 48, height: 48, marginBottom: 12 }} />
          <Title level={3} style={{ marginBottom: 4 }}>
            Set up two-factor authentication
          </Title>
          <Text type="secondary">
            Scan this QR code with Google Authenticator or a compatible app.
          </Text>
        </div>

        {/* QR Code */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={mfaData.qrCodeDataUrl}
            alt="MFA QR code"
            width={200}
            height={200}
            style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}
          />
        </div>

        {/* Manual entry secret */}
        <Card
          size="small"
          style={{ background: '#f5f5f5', marginBottom: 24, borderRadius: 8 }}
          bordered={false}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Or enter this code manually:
          </Text>
          <Text
            code
            copyable
            style={{ fontSize: 16, letterSpacing: 2, wordBreak: 'break-all' }}
          >
            {mfaData.secret}
          </Text>
        </Card>

        {/* Backup codes */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>Backup codes</Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopyBackupCodes}
            >
              {copied ? 'Copied!' : 'Copy all'}
            </Button>
          </div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            Save these codes somewhere safe. Each can be used once if you lose access to your authenticator.
          </Text>
          <Row gutter={[8, 8]}>
            {mfaData.backupCodes.map((code) => (
              <Col span={12} key={code}>
                <Text code style={{ fontSize: 13 }}>{code}</Text>
              </Col>
            ))}
          </Row>
        </div>

        {/* TOTP verification */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Enter the 6-digit code from your authenticator app
          </Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="large"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              style={{ textAlign: 'center', letterSpacing: 8, fontSize: 18 }}
              autoComplete="one-time-code"
            />
            <Button
              type="primary"
              size="large"
              loading={verifyLoading}
              onClick={handleVerify}
              disabled={totpCode.length !== 6}
            >
              Verify &amp; Enable
            </Button>
          </Space.Compact>
        </div>

        <div style={{ textAlign: 'center' }}>
          <Button type="link" size="small" onClick={() => navigate('/')}>
            I&apos;ll set this up later
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default MfaSetupPage;
